import { api } from './client';

export interface ServiceCenterVendor {
  id: string;
  legacyId: number | null;
  name: string;
  description: string | null;
  phones: string | null;
  address: string | null;
  contacts: string | null;
  legacyLog: string | null;
  importedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceCenterRepair {
  id: string;
  legacyId: number | null;
  clientId: number | null;
  client: { id: number; name: string; address: string | null } | null;
  clientName: string;
  deviceName: string;
  deviceOwner: string | null;
  complaint: string | null;
  vendorId: string | null;
  vendor: ServiceCenterVendor | null;
  legacyVendorId: number | null;
  photoPath: string | null;
  hasPhoto: boolean;
  legacyPhotoPath: string | null;
  deliveredToService: boolean;
  deliveredAt: string | null;
  pickedUpFromService: boolean;
  pickedUpAt: string | null;
  documentsReceivedFromService: boolean;
  billedToClient: boolean;
  paidToService: boolean;
  cost: string | null;
  estimatedVendorAmount: string | null;
  vendorAmount: string | null;
  calculatedClientTotal: string | null;
  setupTotal: string | null;
  serviceAnswer: string | null;
  workflowStage: 'NEW' | 'AT_SERVICE' | 'PICKED_UP' | 'READY_FOR_BILLING' | 'BILLED' | 'ARCHIVED';
  lastActivityAt: string;
  inactivityDays: number;
  attentionLevel: 'NONE' | 'YELLOW' | 'RED';
  diagnostics: Array<{
    code: string;
    severity: 'WARNING' | 'ERROR';
    message: string;
  }>;
  attachments: ServiceCenterRepairAttachment[];
  capabilities: {
    canManageDocuments: boolean;
    canManageBilling: boolean;
  };
  isArchived: boolean;
  archivedAt: string | null;
  createdBy: { id: string; name: string | null } | null;
  legacyCreatedByName: string | null;
  legacyUsername: string | null;
  legacyCreatedAt: string | null;
  legacyLastChange: string | null;
  legacyLog: string | null;
  importedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceCenterRepairAttachment {
  id: string;
  repairId: string;
  kind: 'DEVICE_BEFORE' | 'DEVICE_AFTER' | 'SERVICE_INVOICE' | 'SERVICE_ACT' | 'OTHER';
  sourceApp: 'DESKTOP' | 'MOBILE';
  originalName: string;
  mimeType: string;
  extension: string;
  sizeBytes: number;
  uploadedBy: { id: string; name: string | null } | null;
  createdAt: string;
}

export interface ServiceCenterRepairAuditLog {
  id: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  modelName: string;
  recordId: string;
  userId: string | null;
  userName: string | null;
  dataBefore: Record<string, unknown> | null;
  dataAfter: Record<string, unknown> | null;
  diff: Record<string, { old: unknown; new: unknown }> | null;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
}

export interface Vendor {
  id: string;
  legacyId: number | null;
  name: string;
}

export interface RemontEntry {
  id: string;
  legacyId: number | null;
  clientId: number | null;
  name: string;
  company: string;
  whose: string;
  comment: string;
  photo: string;
  hasPhoto: boolean;
  vendor_id: string;
  remont_place: string;
  otvez: number;
  zabral: number;
  cost: string;
  answer: string;
  vistavlenoklientu: number;
  oplacheno: number;
  varhive: number;
  log: string;
  username: string;
  legacyLastChange: string | null;
  legacyCreatedAt: string | null;
  attentionLevel: 'NONE' | 'YELLOW' | 'RED';
  inactivityDays: number;
  workflowStage: ServiceCenterRepair['workflowStage'];
  diagnostics: ServiceCenterRepair['diagnostics'];
  raw: ServiceCenterRepair;
}

export interface RemontOrdersResponse {
  entries: RemontEntry[];
  vendors: Vendor[];
}

export interface SaveRemontDto {
  id?: string;
  clientId?: number;
  name: string;
  company: string;
  whose?: string;
  comment?: string;
  vendor_id?: string;
  otvez?: number;
  zabral?: number;
  estimatedVendorAmount?: number;
  answer?: string;
}

export interface SaveRemontResponse {
  success: boolean;
  id: string;
  action: 'created' | 'updated';
}

function boolToNumber(value: boolean): number {
  return value ? 1 : 0;
}

function mapVendor(vendor: ServiceCenterVendor): Vendor {
  return {
    id: vendor.id,
    legacyId: vendor.legacyId,
    name: vendor.name,
  };
}

function mapRepair(repair: ServiceCenterRepair): RemontEntry {
  return {
    id: repair.id,
    legacyId: repair.legacyId,
    clientId: repair.clientId,
    name: repair.deviceName,
    company: repair.clientName,
    whose: repair.deviceOwner || '',
    comment: repair.complaint || '',
    photo: repair.hasPhoto ? repair.id : '',
    hasPhoto: repair.hasPhoto,
    vendor_id: repair.vendorId || '',
    remont_place: repair.vendor?.name || '',
    otvez: boolToNumber(repair.deliveredToService),
    zabral: boolToNumber(repair.pickedUpFromService),
    cost: repair.estimatedVendorAmount || repair.cost || '',
    answer: repair.serviceAnswer || '',
    vistavlenoklientu: boolToNumber(repair.billedToClient),
    oplacheno: boolToNumber(repair.paidToService),
    varhive: boolToNumber(repair.isArchived),
    log: repair.legacyLog || '',
    username:
      repair.createdBy?.name ||
      repair.legacyCreatedByName ||
      repair.legacyUsername ||
      '',
    legacyLastChange: repair.legacyLastChange,
    legacyCreatedAt: repair.legacyCreatedAt,
    attentionLevel: repair.attentionLevel,
    inactivityDays: repair.inactivityDays,
    workflowStage: repair.workflowStage,
    diagnostics: repair.diagnostics,
    raw: repair,
  };
}

function toFormData(dto: SaveRemontDto, photo?: Blob): FormData {
  const formData = new FormData();
  if (dto.clientId) {
    formData.append('clientId', String(dto.clientId));
  } else {
    formData.append('clientName', dto.company);
  }
  formData.append('deviceName', dto.name);

  const fields: Record<string, string | number | undefined> = {
    deviceOwner: dto.whose,
    complaint: dto.comment,
    vendorId: dto.vendor_id,
    deliveredToService: dto.otvez,
    pickedUpFromService: dto.zabral,
    estimatedVendorAmount: dto.estimatedVendorAmount,
    serviceAnswer: dto.answer,
  };

  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, String(value));
    }
  });

  if (photo) formData.append('photo', photo, 'photo.jpg');
  return formData;
}

const multipartConfig = {
  headers: { 'Content-Type': 'multipart/form-data' },
};

async function getVendors(): Promise<Vendor[]> {
  const { data } = await api.get('/service-center-repairs/vendors');
  return ((data.items || []) as ServiceCenterVendor[]).map(mapVendor);
}

export const serviceCenterRepairsApi = {
  getAll: async (): Promise<RemontOrdersResponse> => {
    const [repairsResponse, vendors] = await Promise.all([
      api.get('/service-center-repairs', {
        params: { status: 'active', ownershipFilter: 'all' },
      }),
      getVendors(),
    ]);

    return {
      entries: ((repairsResponse.data.items || []) as ServiceCenterRepair[]).map(mapRepair),
      vendors,
    };
  },

  getByClient: async (
    clientId: number,
    archive: boolean,
  ): Promise<RemontOrdersResponse> => {
    const [repairsResponse, vendors] = await Promise.all([
      api.get(`/service-center-repairs/client/${clientId}`, {
        params: {
          status: archive ? 'archived' : 'active',
          isArchived: archive,
          ownershipFilter: 'all',
        },
      }),
      getVendors(),
    ]);

    return {
      entries: ((repairsResponse.data.items || []) as ServiceCenterRepair[]).map(mapRepair),
      vendors,
    };
  },

  getByCompanyName: async (
    company: string,
    archive: boolean,
  ): Promise<RemontOrdersResponse> => {
    const [repairsResponse, vendors] = await Promise.all([
      api.get('/service-center-repairs', {
        params: {
          search: company,
          status: archive ? 'archived' : 'active',
          isArchived: archive,
          ownershipFilter: 'all',
        },
      }),
      getVendors(),
    ]);
    const normalizedCompany = company.trim().toLowerCase();

    return {
      entries: ((repairsResponse.data.items || []) as ServiceCenterRepair[])
        .filter((repair) => repair.clientName.trim().toLowerCase() === normalizedCompany)
        .map(mapRepair),
      vendors,
    };
  },

  save: async (
    dto: SaveRemontDto,
    photo?: Blob,
  ): Promise<SaveRemontResponse> => {
    const isUpdate = Boolean(dto.id);
    const response = isUpdate
      ? await api.patch(
          `/service-center-repairs/${dto.id}`,
          toFormData(dto),
          multipartConfig,
        )
      : await api.post('/service-center-repairs', toFormData(dto), multipartConfig);

    if (photo) {
      await serviceCenterRepairsApi.uploadDeviceAttachment(
        response.data.id,
        dto.zabral ? 'DEVICE_AFTER' : 'DEVICE_BEFORE',
        photo,
      );
    }

    return {
      success: true,
      id: response.data.id,
      action: isUpdate ? 'updated' : 'created',
    };
  },

  getHistory: async (id: string) => {
    const { data } = await api.get(`/service-center-repairs/${id}/history`);
    return data as { items: ServiceCenterRepairAuditLog[]; total: number };
  },

  pricingPreview: async (amount: number) => {
    const { data } = await api.get('/service-center-repairs/pricing-preview', {
      params: { amount },
    });
    return data as {
      basePcPrice: string;
      calculatedClientTotal: string;
      reimbursementTotal: string;
      setupTotal: string;
    };
  },

  uploadDeviceAttachment: async (
    id: string,
    kind: 'DEVICE_BEFORE' | 'DEVICE_AFTER' | 'OTHER',
    file: Blob,
  ) => {
    const formData = new FormData();
    formData.append('kind', kind);
    formData.append('sourceApp', 'MOBILE');
    formData.append('file', file, 'photo.jpg');
    const { data } = await api.post(
      `/service-center-repairs/${id}/attachments`,
      formData,
      multipartConfig,
    );
    return data as ServiceCenterRepairAttachment;
  },

  getAttachmentBlobUrl: async (
    repairId: string,
    attachmentId: string,
  ): Promise<string> => {
    const response = await api.get(
      `/service-center-repairs/${repairId}/attachments/${attachmentId}/preview`,
      { responseType: 'blob' },
    );
    return URL.createObjectURL(response.data);
  },

  getPhotoBlobUrl: async (id: string): Promise<string> => {
    const response = await api.get(`/service-center-repairs/${id}/photo`, {
      responseType: 'blob',
    });
    return URL.createObjectURL(response.data);
  },
};
